import type { PortfolioCard as PortfolioCardData } from '@repo/api-contracts';
import useEmblaCarousel from 'embla-carousel-react';
import { type ReactElement, useEffect, useState } from 'react';
import { PortfolioCaseCard } from '@/entities/portfolio';
import { PortfolioCarouselControls } from './portfolio-carousel-controls';

interface PortfolioCarouselProps {
  readonly items: readonly PortfolioCardData[];
  readonly onRetry?: () => void;
}

/** 홈 수동 캐러셀 — 전체 홈 선택을 싣고 화살표·Embla 기본 드래그로 이동한다. */
export const PortfolioCarousel = ({ items, onRetry }: PortfolioCarouselProps): ReactElement => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' });
  const [canScroll, setCanScroll] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (emblaApi === undefined) return undefined;
    const syncScrollState = (): void => {
      setCanScroll(emblaApi.scrollSnapList().length > 1);
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    syncScrollState();
    emblaApi.on('reInit', syncScrollState).on('select', syncScrollState);
    return () => {
      emblaApi.off('reInit', syncScrollState).off('select', syncScrollState);
    };
  }, [emblaApi]);

  return (
    <div className="mt-10 sm:mt-12">
      {/* biome-ignore lint/a11y/useSemanticElements: fieldset은 캐러셀 뷰포트에 맞지 않는다. */}
      <div
        ref={emblaRef}
        role="group"
        aria-roledescription="carousel"
        aria-label="포트폴리오 사례"
        className="overflow-hidden"
      >
        <ul className="-ml-4 flex touch-pan-y touch-pinch-zoom">
          {items.map((item) => (
            <li
              key={item.id}
              className="min-w-0 shrink-0 grow-0 basis-[85%] pl-4 sm:basis-1/2 lg:basis-1/3"
            >
              {/* draggable=false — 브라우저 기본 이미지 끌기가 캐러셀 마우스 끌기를 가로채지 않게. */}
              <PortfolioCaseCard item={item} onRetry={onRetry} draggable={false} />
            </li>
          ))}
        </ul>
      </div>
      {canScroll ? (
        <PortfolioCarouselControls
          canScrollPrev={canScrollPrev}
          canScrollNext={canScrollNext}
          onPrev={() => emblaApi?.scrollPrev()}
          onNext={() => emblaApi?.scrollNext()}
        />
      ) : null}
    </div>
  );
};
